import {TSchema, Type as BaseType, TCodec, TArray, StaticDecode} from "typebox";
import {Format as FormatRegistry} from "typebox/format";
import {
  AccountId,
  ActivityStreamId,
  AssetId,
  BackgroundTaskId,
  CounterpartyId,
  DealId,
  DocumentId,
  DocumentationId,
  ExternalAuthorizationId,
  FileId,
  OwnerId,
  QuestionId,
  QuestionnaireId,
  QuestionGroupId,
  RoleId,
  SectionId,
  StandaloneFileId,
  TeamId,
  UserId,
  Uuid,
  ApikeyId,
  AssessmentControlId,
  AssessmentId,
  AuditPlanPageId,
  ContentId,
  ControlGroupId,
  PredefinedControlId,
  CoreResponseId,
  CrmConnectionId,
  DocumentAccessRequestId,
  DocumentSourceId,
  EntityId,
  ErrorCatalogId,
  EsignatureProviderId,
  EsignatureTemplateId,
  FactClusterId,
  FactId,
  FactTextId,
  FrameworkId,
  FrameworkVersionId,
  LibrarySectionId,
  MailSubscriptionId,
  MailingListId,
  NotificationSinkId,
  QuestionSourceId,
  ResolutionId,
  ScopeAxisId,
  ScopeId,
  SourceChunkId,
  SourceId,
  ThirdPartyId,
  ShowcaseUpdateMessageId,
  TprmExternalQuestionnaireId,
  TprmQuestionnaireTemplateId,
  TprmReportTemplateId,
  PortalReportSnapshotId,
  RiskId,
  TprmScopeId,
  OperationalContextId,
  TprmAssetId,
  ThirdPartyTierId,
  EngagementId,
  ConversationId,
  ComplexityModelId,
  ComplexityModelVersionId,
  ComplexityAssessmentId,
  ComplexityTierId,
  AttributeAssessmentId,
  TprmQuestionnaireModelId,
  DisclosureChecklistModelId,
  DisclosureChecklistTemplateId,
  DisclosureChecklistId,
  UnderstandingId,
  DiagramId,
  PopulationId,
  SampleId,
  TestId,
  SavedViewId,
  ApiDateTime,
} from "platformed-client/types";

// Typebox does not come with any formats out of the box
const UuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/;
const DateTimeRegex = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(?:\.\d+)?Z$/;

FormatRegistry.Set("uuid", (value: string) => UuidRegex.test(value));
FormatRegistry.Set("date-time", (value: string) => DateTimeRegex.test(value));

const GenericUuid = BaseType.String({format: "uuid"});

function NominalType<U>(baseType: TSchema) {
  return BaseType.Unsafe<U>(baseType);
}

export type TSet<S extends TSchema> = TCodec<TArray<S>, Set<StaticDecode<S>>>;

export const Type = {
  ...BaseType,
  Set<S extends TSchema>(schema: S) {
    return this.Codec(this.Array(schema, {uniqueItems: true, default: []}))
      .Decode(value => new Set(value))
      .Encode(value => {
        if (value instanceof Set) {
          return [...value];
        } else {
          return value;
        }
      });
  },
  JsonDate() {
    return this.Codec(this.Number())
      .Decode(value => new Date(value))
      .Encode(value => value.getTime());
  },
  DateTime() {
    return NominalType<ApiDateTime>(BaseType.String({format: "date-time"}));
  },
  Uuid() {
    return NominalType<Uuid>(GenericUuid);
  },
  UserId() {
    return NominalType<UserId>(GenericUuid);
  },
  OwnerId() {
    return NominalType<OwnerId>(GenericUuid);
  },
  QuestionId() {
    return NominalType<QuestionId>(GenericUuid);
  },
  SectionId() {
    return NominalType<SectionId>(GenericUuid);
  },
  QuestionnaireId() {
    return NominalType<QuestionnaireId>(GenericUuid);
  },
  QuestionGroupId() {
    return NominalType<QuestionGroupId>(GenericUuid);
  },
  AccountId() {
    return NominalType<AccountId>(GenericUuid);
  },
  CounterpartyId() {
    return NominalType<CounterpartyId>(GenericUuid);
  },
  DocumentId() {
    return NominalType<DocumentId>(GenericUuid);
  },
  FileId() {
    return NominalType<FileId>(GenericUuid);
  },
  StandaloneFileId() {
    return NominalType<StandaloneFileId>(GenericUuid);
  },
  DocumentationId() {
    return NominalType<DocumentationId>(GenericUuid);
  },
  TeamId() {
    return NominalType<TeamId>(GenericUuid);
  },
  RoleId() {
    return NominalType<RoleId>(GenericUuid);
  },
  DealId() {
    return NominalType<DealId>(GenericUuid);
  },
  ExternalAuthorizationId() {
    return NominalType<ExternalAuthorizationId>(GenericUuid);
  },
  AssetId() {
    return NominalType<AssetId>(GenericUuid);
  },
  ActivityStreamId() {
    return NominalType<ActivityStreamId>(GenericUuid);
  },
  BackgroundTaskId() {
    return NominalType<BackgroundTaskId>(GenericUuid);
  },
  ApikeyId() {
    return NominalType<ApikeyId>(GenericUuid);
  },
  FactTextId() {
    return NominalType<FactTextId>(GenericUuid);
  },
  FactClusterId() {
    return NominalType<FactClusterId>(GenericUuid);
  },
  ScopeId() {
    return NominalType<ScopeId>(GenericUuid);
  },
  ScopeAxisId() {
    return NominalType<ScopeAxisId>(GenericUuid);
  },
  EntityId() {
    return NominalType<EntityId>(GenericUuid);
  },
  ComplexityTierId() {
    return NominalType<ComplexityTierId>(GenericUuid);
  },
  ContentId() {
    return NominalType<ContentId>(GenericUuid);
  },
  ResolutionId() {
    return NominalType<ResolutionId>(GenericUuid);
  },
  ErrorCatalogId() {
    return NominalType<ErrorCatalogId>(GenericUuid);
  },
  DocumentAccessRequestId() {
    return NominalType<DocumentAccessRequestId>(GenericUuid);
  },
  CrmConnectionId() {
    return NominalType<CrmConnectionId>(GenericUuid);
  },
  DocumentSourceId() {
    return NominalType<DocumentSourceId>(GenericUuid);
  },
  EsignatureProviderId() {
    return NominalType<EsignatureProviderId>(GenericUuid);
  },
  EsignatureTemplateId() {
    return NominalType<EsignatureTemplateId>(GenericUuid);
  },
  NotificationSinkId() {
    return NominalType<NotificationSinkId>(GenericUuid);
  },
  QuestionSourceId() {
    return NominalType<QuestionSourceId>(GenericUuid);
  },
  LibrarySectionId() {
    return NominalType<LibrarySectionId>(GenericUuid);
  },
  FactId() {
    return NominalType<FactId>(GenericUuid);
  },
  CoreResponseId() {
    return NominalType<CoreResponseId>(GenericUuid);
  },
  FrameworkId() {
    return NominalType<FrameworkId>(GenericUuid);
  },
  FrameworkVersionId() {
    return NominalType<FrameworkVersionId>(GenericUuid);
  },
  ThirdPartyId() {
    return NominalType<ThirdPartyId>(GenericUuid);
  },
  AssessmentControlId() {
    return NominalType<AssessmentControlId>(GenericUuid);
  },
  AssessmentId() {
    return NominalType<AssessmentId>(GenericUuid);
  },
  AuditPlanPageId() {
    return NominalType<AuditPlanPageId>(GenericUuid);
  },
  PredefinedControlId() {
    return NominalType<PredefinedControlId>(GenericUuid);
  },
  ControlGroupId() {
    return NominalType<ControlGroupId>(GenericUuid);
  },
  SourceId() {
    return NominalType<SourceId>(GenericUuid);
  },
  SourceChunkId() {
    return NominalType<SourceChunkId>(GenericUuid);
  },
  MailingListId() {
    return NominalType<MailingListId>(GenericUuid);
  },
  ShowcaseUpdateMessageId() {
    return NominalType<ShowcaseUpdateMessageId>(GenericUuid);
  },
  MailSubscriptionId() {
    return NominalType<MailSubscriptionId>(GenericUuid);
  },
  TprmExternalQuestionnaireId() {
    return NominalType<TprmExternalQuestionnaireId>(GenericUuid);
  },
  TprmQuestionnaireTemplateId() {
    return NominalType<TprmQuestionnaireTemplateId>(GenericUuid);
  },
  TprmQuestionnaireModelId() {
    return NominalType<TprmQuestionnaireModelId>(GenericUuid);
  },
  DisclosureChecklistModelId() {
    return NominalType<DisclosureChecklistModelId>(GenericUuid);
  },
  DisclosureChecklistTemplateId() {
    return NominalType<DisclosureChecklistTemplateId>(GenericUuid);
  },
  DisclosureChecklistId() {
    return NominalType<DisclosureChecklistId>(GenericUuid);
  },
  TprmReportTemplateId() {
    return NominalType<TprmReportTemplateId>(GenericUuid);
  },
  PortalReportSnapshotId() {
    return NominalType<PortalReportSnapshotId>(GenericUuid);
  },
  RiskId() {
    return NominalType<RiskId>(GenericUuid);
  },
  TprmScopeId() {
    return NominalType<TprmScopeId>(GenericUuid);
  },
  OperationalContextId() {
    return NominalType<OperationalContextId>(GenericUuid);
  },
  ThirdPartyTierId() {
    return NominalType<ThirdPartyTierId>(GenericUuid);
  },
  TprmAssetId() {
    return NominalType<TprmAssetId>(GenericUuid);
  },
  EngagementId() {
    return NominalType<EngagementId>(GenericUuid);
  },
  ConversationId() {
    return NominalType<ConversationId>(GenericUuid);
  },
  ComplexityModelId() {
    return NominalType<ComplexityModelId>(GenericUuid);
  },
  ComplexityModelVersionId() {
    return NominalType<ComplexityModelVersionId>(GenericUuid);
  },
  ComplexityAssessmentId() {
    return NominalType<ComplexityAssessmentId>(GenericUuid);
  },
  AttributeAssessmentId() {
    return NominalType<AttributeAssessmentId>(GenericUuid);
  },
  UnderstandingId() {
    return NominalType<UnderstandingId>(GenericUuid);
  },
  DiagramId() {
    return NominalType<DiagramId>(GenericUuid);
  },
  PopulationId() {
    return NominalType<PopulationId>(GenericUuid);
  },
  SampleId() {
    return NominalType<SampleId>(GenericUuid);
  },
  TestId() {
    return NominalType<TestId>(GenericUuid);
  },
  SavedViewId() {
    return NominalType<SavedViewId>(GenericUuid);
  },
};
